import classNames from 'classnames';
import React from 'react';
import * as styles from './Stats.module.scss';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import checkIcon from '../icons/non-prefilled/16x16/check.svg';
import commentIcon from '../icons/non-prefilled/16x16/comment.svg';
import textFormatIcon from '../icons/non-prefilled/16x16/textFormat.svg';
import formatNumberCompact from '../utils/formatNumberCompact';

interface StatGroupProps {
  label: React.ReactNode;
  children: React.ReactNode;
}

const StatGroup = ({ label, children }: StatGroupProps) => (
  <span className={styles.statGroup}>
    <span>{label}</span>
    {children}
  </span>
);

interface StatProps {
  icon: React.ReactNode;
  children: number;
  variant: 'success' | 'danger' | 'warning' | 'muted';
}

const Stat = ({ icon, children, variant }: StatProps) => {
  if (children === 0) {
    return null;
  }

  return (
    <span className={classNames(styles.stat, [styles[variant]])}>
      <span>{icon}</span>
      <span>{formatNumberCompact(children)}</span>
    </span>
  );
};

const getWordCount = (count: number) => Math.ceil(count / 5);

const STATS_FRAGMENT = gql(/** @complexity 1 */ `
  fragment Stats_stats on HistoryStats {
    insert
    delete
    comment
    resolve
  }
`);

interface StatsProps {
  stats: DocumentType<typeof STATS_FRAGMENT>;
}

const Stats = ({ stats }: StatsProps) => {
  return (
    <span className={styles.container}>
      {(!!stats.insert || !!stats.delete) && (
        <StatGroup
          label={
            <SVGIcon aria-label="Edits" size={14}>
              {textFormatIcon}
            </SVGIcon>
          }
        >
          <Stat
            variant="success"
            icon={<span className={styles.plusMinusSign}>+</span>}
          >
            {getWordCount(stats.insert)}
          </Stat>
          <Stat
            variant="danger"
            icon={<span className={styles.plusMinusSign}>-</span>}
          >
            {getWordCount(stats.delete)}
          </Stat>
        </StatGroup>
      )}

      {(!!stats.comment || !!stats.resolve) && (
        <StatGroup
          label={
            <SVGIcon aria-label="Comments" size={12}>
              {commentIcon}
            </SVGIcon>
          }
        >
          <Stat
            variant="warning"
            icon={<span className={styles.plusMinusSign}>+</span>}
          >
            {stats.comment}
          </Stat>
          <Stat
            variant="muted"
            icon={
              <SVGIcon inline size={8} className={styles.checkIcon}>
                {checkIcon}
              </SVGIcon>
            }
          >
            {stats.resolve}
          </Stat>
        </StatGroup>
      )}
    </span>
  );
};

export default Stats;
